var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { formatCurrency } from '@backrs_ui/common-ui/src/utils/stringFormatters.util';
import { Box, Chip, List, ListItem, Typography } from '@mui/material';
import { ROIHorizonModalContent } from './ROIHorizonModalContent';
import { ROIModalCard } from './ROIModalCard';
import { InfoModal } from '../InfoModal';
var ROI_LINE_CHART_STRINGS = appStrings.modules.charts.returnOnInvestment;
var VERY_GOOD_ROI_THRESHOLD = 400000;
var GOOD_ROI_THRESHOLD = 200000;
var getChipIcon = function (_a) {
    var total_roi = _a.total_roi, onClick = _a.onClick;
    var chipType = (function () {
        if (total_roi >= VERY_GOOD_ROI_THRESHOLD)
            return 'veryGoodROI';
        if (total_roi >= GOOD_ROI_THRESHOLD)
            return 'goodROI';
        return 'lowROI';
    })();
    return (_jsx(Chip, { variant: "badge", size: "md", type: "leading", label: ROI_LINE_CHART_STRINGS.modalCards[chipType].chartChipLabel, icon: _jsx(AppIcon, { iconName: chipType === 'lowROI' ? 'ThumbsDown' : 'ThumbsUp', diameter: "20px", color: ROI_LINE_CHART_STRINGS.modalCards[chipType].chipColor }), sx: chartChipSx, onClick: onClick }));
};
export var ROILineChartHeader = function (_a) {
    var total_roi = _a.total_roi;
    var _b = useState(false), isROIHorizonModalOpen = _b[0], setIsROIHorizonModalOpen = _b[1];
    var _c = useState(false), isROILevelsModalOpen = _c[0], setIsROILevelsModalOpen = _c[1];
    return (_jsxs(_Fragment, { children: [_jsx(InfoModal, { open: isROIHorizonModalOpen, onClose: function () { return setIsROIHorizonModalOpen(false); }, headerContent: _jsx(Typography, { variant: "text-lg-semibold", children: ROI_LINE_CHART_STRINGS.roiHorizonModal.title }), bodyContent: _jsx(ROIHorizonModalContent, {}) }), _jsx(InfoModal, { open: isROILevelsModalOpen, onClose: function () { return setIsROILevelsModalOpen(false); }, headerContent: _jsxs(Box, { sx: headerContentContainerSx, children: [_jsx(Typography, { variant: "text-lg-semibold", color: function (_a) {
                                var backrs = _a.backrs;
                                return backrs.colors['Colors/Text/text-primary'];
                            }, children: ROI_LINE_CHART_STRINGS.explanationTitle }), _jsxs(Box, { sx: sectionSx, children: [_jsx(Typography, { variant: "text-sm-bold", children: ROI_LINE_CHART_STRINGS.whatItIs.title }), ROI_LINE_CHART_STRINGS.whatItIs.paragraphs.map(function (paragraph, index) { return (_jsx(Typography, { variant: "text-sm-regular", sx: paragraphSx, children: paragraph }, index)); })] }), _jsxs(Box, { sx: sectionSx, children: [_jsx(Typography, { variant: "text-sm-bold", children: ROI_LINE_CHART_STRINGS.whyItMatters.title }), ROI_LINE_CHART_STRINGS.whyItMatters.paragraphs.map(function (paragraph, index) { return (_jsx(Typography, { variant: "text-sm-regular", sx: paragraphSx, children: paragraph }, index)); }), _jsx(List, { sx: bulletListSx, children: ROI_LINE_CHART_STRINGS.whyItMatters.bulletPoints.map(function (point, index) { return (_jsx(ListItem, { sx: bulletItemSx, children: _jsx(Typography, { variant: "text-sm-regular", children: point }) }, index)); }) })] }), _jsx(Typography, { variant: "text-sm-bold", children: ROI_LINE_CHART_STRINGS.howToInterpret.title })] }), bodyContent: _jsx(Box, { sx: bodyContentSx, children: Object.values(ROI_LINE_CHART_STRINGS.modalCards).map(function (card) { return (_jsx(ROIModalCard, { title: card.title, chipLabelPrefix: card.chipLabelPrefix, chipLabelThreshold: card.chipLabelThreshold, chipColor: card.chipColor, iconName: card.iconName, cardDescriptions: card.cardDescription }, card.title)); }) }) }), _jsxs(Box, { sx: titleContentSx, children: [_jsxs(Box, { sx: titleSx, children: [_jsx(Typography, { variant: "text-lg-semibold", component: "div", children: ROI_LINE_CHART_STRINGS.title }), _jsx(Box, { sx: tooltipIconSx, onClick: function () { return setIsROIHorizonModalOpen(true); }, children: _jsx(AppIcon, { iconName: "HelpCircle", diameter: "20px", color: "Colors/Foreground/fg-quinary" }) })] }), _jsxs(Box, { sx: totalInvestmentSx, children: [_jsx(Typography, { variant: "display-sm-semibold", component: "div", children: formatCurrency(total_roi) }), getChipIcon({ total_roi: total_roi, onClick: function () { return setIsROILevelsModalOpen(true); } })] })] })] }));
};
var titleContentSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        flexDirection: 'column',
        gap: backrs.spacing['spacing-md'],
    });
};
var totalInvestmentSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: backrs.spacing['spacing-lg'],
        mb: { xs: backrs.spacing['spacing-2xl'], sm: backrs.spacing['spacing-none'] },
    });
};
var titleSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        flexDirection: { xs: 'row', sm: 'row' },
        justifyContent: 'space-between',
        gap: backrs.spacing['spacing-sm'],
    });
};
var tooltipIconSx = {
    cursor: 'pointer',
};
var chartChipSx = function (_a) {
    var typography = _a.typography, backrs = _a.backrs;
    return ({
        border: "1px solid ".concat(backrs.colors['Colors/Border/border-primary']),
        cursor: 'pointer',
        '& .MuiChip-label': __assign({}, typography['text-lg-medium']),
    });
};
var bodyContentSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        flexDirection: 'column',
        gap: backrs.spacing['spacing-lg'],
    });
};
var headerContentContainerSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        flexDirection: 'column',
        gap: backrs.spacing['spacing-lg'],
    });
};
var sectionSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        flexDirection: 'column',
        gap: backrs.spacing['spacing-xs'],
    });
};
var paragraphSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        color: backrs.colors['Colors/Text/text-secondary'],
    });
};
var bulletListSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        margin: 0,
        paddingLeft: backrs.spacing['spacing-2xl'],
        listStyleType: 'disc',
    });
};
var bulletItemSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'list-item',
        padding: 0,
        paddingLeft: backrs.spacing['spacing-xs'],
        color: backrs.colors['Colors/Text/text-secondary'],
    });
};
